import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import WcIcon from "@mui/icons-material/Wc";
import { useDispatch } from "react-redux";
import { getSingleUserData, getSubscriptionHistory } from "store/actions";
import { useLocation, useNavigate } from "react-router-dom";
import { getInitials } from "utils/constants";
import moment from "moment";

const ViewUser = () => {
  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location?.state?.id;

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  const fetchUserDetails = () => {
    dispatch(
      getSingleUserData(userId, (result) => {
        if (result) {
          setUserDetails(result);
        }
      })
    );

    // fetch subscription history for this user (server accepts ?userId for admins)
    dispatch(
      getSubscriptionHistory(userId, (history) => {
        setSubscriptionHistory(history || []);
      })
    );
  };

  const handleBack = () => {
    navigate("/admin/users");
  };

  if (!userDetails) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Loading user details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        Back to Users
      </Button>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: "center", p: 4 }}>
            <Avatar
              src={userDetails?.profileImage?.signedUrl || undefined}
              sx={{ width: 120, height: 120, mx: "auto", fontSize: 48 }}
            >
              {!userDetails?.profileImage?.signedUrl &&
                getInitials(userDetails?.name)}
            </Avatar>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {userDetails?.name}
            </Typography>
            <Chip
              label={userDetails?.isActive ? "Active" : "Inactive"}
              color={userDetails?.isActive ? "success" : "error"}
              size="small"
              sx={{ mb: 2 }}
            />
            <CardContent sx={{ mt: 2, textAlign: "left" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MailOutlineIcon sx={{ mr: 2, color: "action.active" }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">
                    {userDetails?.email}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PhoneIphoneIcon sx={{ mr: 2, color: "action.active" }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Mobile Number
                  </Typography>
                  <Typography variant="body2">
                    {userDetails?.mobileNumber || "N/A"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 2, color: "action.active" }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Role
                  </Typography>
                  <Typography variant="body2">
                    {userDetails?.role || "User"}
                  </Typography>
                </Box>
              </Box>
              {userDetails?.gender && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <WcIcon sx={{ mr: 2, color: "action.active" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Gender
                    </Typography>
                    <Typography variant="body2">
                      {userDetails?.gender}
                    </Typography>
                  </Box>
                </Box>
              )}
              {userDetails?.dateOfBirth && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <CakeIcon sx={{ mr: 2, color: "action.active" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body2">
                      {moment(userDetails?.dateOfBirth).format("DD MMM YYYY")}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Account Created
                    </Typography>
                    <Typography variant="body2">
                      {moment(userDetails?.createdAt).format("DD MMM YYYY, hh:mm A")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {moment(userDetails?.updatedAt).format("DD MMM YYYY, hh:mm A")}
                    </Typography>
                  </Box>
                </Grid>
                {userDetails?.bio && (
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bio
                      </Typography>
                      <Typography variant="body2">
                        {userDetails?.bio}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Subscription history */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Subscription History
              </Typography>

              {subscriptionHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No subscriptions found.
                </Typography>
              ) : (
                subscriptionHistory.map((s) => (
                  <Box key={s._id || s.purchaseId} sx={{ mb: 2, pb: 1, borderBottom: "1px solid #eee" }}>
                    <Typography variant="subtitle2">
                      {s.planType ? s.planType.toUpperCase() : "Plan"} • {s.billingCycle || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount: {s.amount ?? "N/A"} {s.currency ?? ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Purchase: {s.purchaseDate ? moment(s.purchaseDate).format("DD MMM YYYY, hh:mm A") : "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Expiry: {s.expiryDate ? moment(s.expiryDate).format("DD MMM YYYY, hh:mm A") : "N/A"}
                    </Typography>
                    <Chip label={s.status || "unknown"} size="small" sx={{ mt: 1 }} />
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewUser;
